import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import { nextStep, setExperience } from '../../redux/slice/findJobSlice';
import { fetchSkills } from '../../redux/action/commonAction';
import SkillCard from '../../components/hire-developer/SkillCard';
import Button from '../../components/general/Button';
import Input from '../../components/general/Input';
import { toast } from 'react-toastify';
import useDebounce from '../../hooks/useDebounce';
import DashedChips from '../../components/common/DashedChips';

const StepFiveForm = () => {
    const { control, reset, setValue } = useForm();

    const dispatch = useDispatch()
    const { experience } = useSelector(state => state.findJob.stepFiveData);
    const { isTechLoading, technologies } = useSelector(state => state.common.tech);
    const [activeTechs, setActiveTechs] = useState(new Set([]))
    const [displayActiveTechs, setDisplayActiveTechs] = useState([])
    const [search, setSearch] = useState('')
    const debounce = useDebounce();
    
    useEffect(() => {
        if (!isTechLoading) return;
        dispatch(fetchSkills(30));
        // eslint-disable-next-line
    }, [isTechLoading])

    const activeTechList = useMemo(() => {
        return Object.keys(experience).length
    }, [experience])

    const filteredTechnologies = useMemo(() => {
        return technologies.filter(tech => tech.name.toLowerCase().includes(search.toLowerCase()))
    }, [technologies, search])

    useEffect(() => {
        Object.keys(experience).forEach(techName => {
            setValue(techName, experience[techName] || null);
        });
        // eslint-disable-next-line
    }, [experience, reset, setValue])

    const handleSkillChange = (tech, toggle, experience = null) => {
        const currentActiveTechs = new Set(activeTechs);
        if (toggle) {
            // Remove skill
            currentActiveTechs.delete(tech.id);
            setDisplayActiveTechs(prev => prev.filter(t => t.name !== tech.name));
        } else {
            // Add or update skill
            setDisplayActiveTechs(prev => {
                const techIndex = prev.findIndex(t => t.name === tech.name);
                if (techIndex !== -1) {
                    // Update experience if provided
                    const updated = [...prev];
                    if (experience !== null) {
                        updated[techIndex].experience = experience;
                    }
                    return updated;
                } else {
                    // Add new skill
                    return [...prev, { name: tech.name, experience: experience }];
                }
            });
            currentActiveTechs.add(tech.id);
        }
        setActiveTechs(currentActiveTechs);
        dispatch(setExperience({ tech: tech.id, toggle: toggle, years: experience }));
    }

    const handleSubmit = () => {
        if(!Object.keys(experience).length) {
            toast.error("Please select at least one technology")
            return;
        }

        const isExperienceNull = Object.values(experience).some(value => value === null);

        if(isExperienceNull) {
            toast.error("Some of the experiences fields are empty.")
            return;
        }

        dispatch(nextStep())
    }

    const handleInputChange = (text) => {
        debounce(() => {
            dispatch(fetchSkills(30, text));
        }, 500);
    }

    return (
        <div className='min-h-[60vh] !py-[50px] !pb-[50px] lg:flex lg:justify-between'>
            <div className=''>
                <div>
                    <p className='text-primary-color'> 5/8 </p>
                    <h1 className="text-[1.6rem] font-semibold">
                        <span> Choose your main </span>
                        {/* <Image src={DoubleStar} alt="double-star" className="hidden lg:inline-block" /> */}
                        <span className='lg:block'> technologies?</span>

                    </h1>
                </div>

                <div className='hidden lg:block mt-[20px] text-[#2A2A2A]'>
                    <div className='mt-[20px]'>
                        <p> 6/8 </p>
                        <h1 className="text-[1.6rem] font-semibold">
                            <span> Add your experience </span>
                            <span className='lg:block'> highlights </span>
                        </h1>
                    </div>
                    <div className='mt-[20px]'>
                        <p> 7/8 </p>
                        <h1 className="text-[1.6rem] font-semibold">
                            <span> Ready to pass an </span>
                            <span className='lg:block'> interview in English? </span>
                        </h1>
                    </div>
                </div>
            </div>

            <div className='lg:w-[55%]'>
                <div className='hidden lg:flex gap-[20px] text-[13px] mb-[40px] lg:justify-center'>
                    <p className='text-primary-color'> 1. Project Preferences </p>
                    <p className='text-primary-color'> 2. Specialization in stack </p>
                    <p className='text-primary-color'> 3. Additional info </p>
                    <p> 4. Registration </p>
                </div>

                <div className='text-[15px] mb-[30px]'>
                    <div className='flex items-center justify-center gap-[10px] text-[#797979]'>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 hidden lg:block">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                        </svg>

                        <div className='text-left lg:text-center'>
                            Indicate your 4 present core technologies, specifying the amount
                        </div>
                    </div>
                    <div className='text-left lg:text-center text-[#797979]'>
                        of recent practical experience with each of them
                    </div>
                </div>

                <div>
                    <Input 
                        placeholder='Search' 
                        onChange={handleInputChange}
                    />
                </div>
                <div className='flex flex-wrap gap-2'>
                    {
                        displayActiveTechs.map((tech, index) => (
                                tech.experience ? (
                                    <DashedChips keyName={index} text={`${tech.name}-${tech.experience} years`} />
                                ) : (
                                <DashedChips keyName={index} text={`${tech.name}`} />
                            )
                        ))
                    }
                </div>
                <div className='lg:max-h-[200px] lg:overflow-y-auto'>
                    <div className='grid grid-cols-2 md:grid-cols-4 lg:flex lg:flex-wrap lg:items-center lg:justify-between gap-4 mt-[20px] w-full'>
                        {
                            filteredTechnologies.map((tech, index) => (
                                <SkillCard
                                    supportsExperience={true}
                                    onExperienceChange={(value) => handleSkillChange(tech, false, value)}
                                    extraClass='flex-col lg:w-[100px] lg:min-h-[100px] justify-center'
                                    onClick={() => handleSkillChange(tech, activeTechs.has(tech.id))} keyName={index} icon={tech.icon_details ? tech.icon_details.file : ''} name={tech.name} isActive={activeTechs.has(tech.id)} 
                                />
                            ))
                        }
                    </div>
                </div>
                <Button
                    disabled={!(activeTechList >= 2)}
                    onClick={handleSubmit}
                    className='w-full bg-primary-color text-black h-[40px] lg:h-[50px] text-[1rem] lg:text-[1rem] lg:mx-auto rounded mt-[20px] md:w-[200px] lg:mt-[50px]' >
                        Ok, next <i className="fa-solid fa-arrow-up-right" />
                </Button>
            </div>
        </div>
    )
}

export default StepFiveForm